"use client";

import React, {
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
  type RefObject,
} from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

interface ScrollFloatProps {
  children: ReactNode;
  id?: string;
  triggerRef?: RefObject<HTMLElement | null>;
  scrollContainerRef?: RefObject<HTMLElement | null>;
  containerClassName?: string;
  textClassName?: string;
  animationDuration?: number;
  ease?: string;
  scrollStart?: string;
  scrollEnd?: string;
  stagger?: number;
  axis?: "x" | "y";
  scrub?: boolean | number;
}

const ScrollFloat: React.FC<ScrollFloatProps> = ({
  children,
  id,
  triggerRef,
  scrollContainerRef,
  containerClassName = "",
  textClassName = "",
  animationDuration = 1,
  ease = "back.inOut(2)",
  scrollStart = "center bottom+=50%",
  scrollEnd = "bottom bottom-=40%",
  stagger = 0.03,
  axis = "y",
  scrub = true,
}) => {
  const containerRef = useRef<HTMLHeadingElement>(null);
  const textRef = useRef<HTMLSpanElement>(null);

  const splitText = useMemo(() => {
    const text = typeof children === "string" ? children : "";
    return text.split("").map((character, index) => (
      <span
        className={`inline-block word ${textClassName}`}
        key={`${character}-${index}`}
      >
        {character === " " ? " " : character}
      </span>
    ));
  }, [children]);

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    gsap.registerPlugin(ScrollTrigger);

    const scroller = scrollContainerRef?.current ?? window;
    const characters = element.querySelectorAll(".word");
    const isHorizontal = axis === "x";

    const context = gsap.context(() => {
      const scrollTrigger = {
        trigger: triggerRef?.current ?? element,
        scroller,
        start: scrollStart,
        end: scrollEnd,
        scrub,
      };

      if (isHorizontal && textRef.current) {
        const timeline = gsap.timeline({ scrollTrigger });
        timeline.fromTo(
          textRef.current,
          { x: () => element.offsetWidth },
          { x: 0, duration: animationDuration, ease },
          0,
        );
        timeline.fromTo(
          characters,
          {
            willChange: "opacity, transform",
            opacity: 0,
            scaleX: 0.7,
            scaleY: 2.3,
            transformOrigin: "50% 50%",
          },
          {
            opacity: 1,
            scaleX: 1,
            scaleY: 1,
            duration: animationDuration,
            ease,
            stagger,
          },
          0,
        );
        timeline.to(
          textRef.current,
          { x: () => -element.offsetWidth, duration: animationDuration, ease },
          animationDuration * 2,
        );
        timeline.to(
          characters,
          { opacity: 0, duration: animationDuration, ease, stagger },
          animationDuration * 2,
        );
      } else {
        gsap.fromTo(
          characters,
          {
            willChange: "opacity, transform",
            opacity: 0,
            yPercent: 120,
            scaleY: 2.3,
            scaleX: 0.7,
            transformOrigin: "50% 0%",
          },
          {
            duration: animationDuration,
            ease,
            opacity: 1,
            yPercent: 0,
            scaleY: 1,
            scaleX: 1,
            stagger,
            scrollTrigger,
          },
        );
      }
    }, element);

    return () => {
      context.revert();
    };
  }, [animationDuration, axis, ease, scrollContainerRef, scrollEnd, scrollStart, stagger, triggerRef, scrub]);

  return (
    <h2 id={id} ref={containerRef} className={`my-5 overflow-hidden ${containerClassName}`}>
      <span ref={textRef} className="inline-block">
        {splitText}
      </span>
    </h2>
  );
};

export default ScrollFloat;
